// SPDX-License-Identifier: AGPL-3.0-or-later

import {ComparisonCheckRow} from '@app/features/app/components/dialogs/components/ComparisonCheckRow';
import {ComparisonRow} from '@app/features/app/components/dialogs/components/ComparisonRow';
import styles from '@app/features/app/components/dialogs/components/FeatureComparisonTable.module.css';
import {FLUXER_TAG_DIGIT_COUNT, PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Limits} from '@app/features/app/utils/UserLimits';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {
	isBooleanPerk,
	isNumericPerk,
	isTextPerk,
	PLUTONIUM_PERKS,
	type PlutoniumPerk,
} from '@app/vendor/constants/PlutoniumPerks';
import {formatNumber} from '@app/vendor/number_utils/NumberFormatting';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useMemo} from 'react';
export const FeatureComparisonTable = observer(() => {
	const {t, i18n} = useLingui();
	const locale = i18n.locale;
	const perkLabels = useMemo(
		() => ({
			custom_4_digit_username_tag: t`Custom ${FLUXER_TAG_DIGIT_COUNT}-digit username tag`,
			per_community_profiles: t`Per-community profiles`,
			message_scheduling: t`Message scheduling`,
			profile_badge: t`Profile badge`,
			custom_video_backgrounds: t`Custom video backgrounds`,
			entrance_sounds: t`Entrance sounds`,
			communities: t`Communities`,
			message_character_limit: t`Message character limit`,
			bookmarked_messages: t`Bookmarked messages`,
			file_upload_size: t`File upload size`,
			emoji_sticker_packs: t`Emoji & sticker packs`,
			saved_media: t`Saved media`,
			use_animated_emojis: t`Use animated emojis`,
			global_emoji_sticker_access: t`Global emoji & sticker access`,
			video_quality: t`Video quality`,
			animated_avatars_and_banners: t`Animated avatars & profile banners`,
			early_access: t`Early access to new features`,
			custom_themes: t`Custom themes`,
			video_quality_free: t`720p/30fps`,
			video_quality_premium: t`Up to 4K/60fps`,
		}),
		[t],
	);
	const availablePerks = useMemo(() => PLUTONIUM_PERKS.filter((perk) => perk.status === 'available'), []);
	const formatPerkValue = (perk: PlutoniumPerk, value: number, isPremium: boolean): string => {
		if (!isNumericPerk(perk)) return String(value);
		const resolvedValue = perk.limitKey
			? isPremium
				? Limits.getPremiumValue(perk.limitKey, value)
				: Limits.getFreeValue(perk.limitKey, value)
			: value;
		if (perk.unit === 'bytes') {
			return formatFileSize(resolvedValue);
		}
		return formatNumber(resolvedValue, locale);
	};
	const renderPerkRow = (perk: PlutoniumPerk) => {
		const label = perkLabels[perk.i18nKey as keyof typeof perkLabels] || perk.i18nKey;
		if (isBooleanPerk(perk)) {
			return (
				<ComparisonCheckRow key={perk.id} feature={label} freeHas={perk.freeValue} plutoniumHas={perk.plutoniumValue} />
			);
		}
		if (isNumericPerk(perk)) {
			return (
				<ComparisonRow
					key={perk.id}
					feature={label}
					freeValue={formatPerkValue(perk, perk.freeValue, false)}
					plutoniumValue={formatPerkValue(perk, perk.plutoniumValue, true)}
				/>
			);
		}
		if (isTextPerk(perk)) {
			const freeLabel = perkLabels[perk.freeValueI18nKey as keyof typeof perkLabels] || perk.freeValueI18nKey;
			const premiumLabel =
				perkLabels[perk.plutoniumValueI18nKey as keyof typeof perkLabels] || perk.plutoniumValueI18nKey;
			return <ComparisonRow key={perk.id} feature={label} freeValue={freeLabel} plutoniumValue={premiumLabel} />;
		}
		return null;
	};
	return (
		<div className={styles.table}>
			<div className={styles.header}>
				<div className={styles.headerFeature}>
					<p className={styles.headerFeatureText}>
						<Trans>Feature</Trans>
					</p>
				</div>
				<div className={styles.headerValues}>
					<div className={styles.headerFree}>
						<Trans>Free</Trans>
					</div>
					<div className={styles.headerPlutonium}>{PREMIUM_PRODUCT_NAME}</div>
				</div>
			</div>
			<div className={styles.rows}>{availablePerks.map(renderPerkRow)}</div>
		</div>
	);
});
